/**
 * Application routing configuration
 * Defines all routes with automatic error boundaries
 */
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@components/common';
import { LoadingFallback } from '@components/common/LoadingFallback/LoadingFallback';
import { ProtectedRoute } from '@components/common/ProtectedRoute/ProtectedRoute';
import MainLayout from '@components/layout/MainLayout/MainLayout';
import AuthLayout from '@components/layout/AuthLayout/AuthLayout';
import { ROUTES } from '@core/constants/routes.constants';
import { wrapRoute } from './RouteWrapper';

// Lazy load pages
const Login = lazy(() => import('@features/auth/pages/Login'));
const Register = lazy(() => import('@features/auth/pages/Register'));
const Profile = lazy(() => import('@features/profile/pages/Profile'));
const Settings = lazy(() => import('@features/settings/pages/Settings'));
const ComponentsShowcase = lazy(() => import('@features/showcase/pages/ComponentsShowcase'));
const ArchitectureDocs = lazy(() => import('@features/documentation/pages/ArchitectureDocs'));
const UserManagement = lazy(() => import('@features/users/pages/UserManagement'));
const Templates = lazy(() => import('@features/templates/pages/Templates'));
const TemplateDetail = lazy(() => import('@features/templates/pages/TemplateDetail'));
const TemplateCategoryConfig = lazy(() => import('@features/template-categories/pages/TemplateCategoryConfig'));
const SystemPrompt = lazy(() => import('@features/system-prompt/pages/SystemPrompt'));
const ReferenceListConfiguration = lazy(() => import('@features/reference-lists/pages/ReferenceListConfiguration'));
const ReferenceListForm = lazy(() => import('@features/reference-lists/pages/ReferenceListForm'));
const ReferenceListData = lazy(() => import('@features/reference-lists/pages/ReferenceListData'));
const ZoneConfigurationList = lazy(() => import('@features/zone-configuration/pages/ZoneConfigurationList'));
const ZoneConfigurationForm = lazy(() => import('@features/zone-configuration/pages/ZoneConfigurationForm'));
const RulesManagement = lazy(() => import('@features/rules-management/pages/RulesManagement'));
const Unauthorized = lazy(() => import('@features/error/pages/Unauthorized'));
const NotFound = lazy(() => import('@features/error/pages/NotFound'));
if (typeof window !== 'undefined' && !window._env_) {
    window._env_ = {};
  }
/**
 * Application routes component
 */
export const AppRoutes: React.FC = () => {
    return (
        <ErrorBoundary componentName="Application Routes" showDetails={import.meta.env.DEV}>
            <Suspense fallback={<LoadingFallback fullScreen />}>
                <Routes>
                    {/* Public routes */}
                    <Route element={<AuthLayout />}>
                        <Route path={ROUTES.LOGIN} element={wrapRoute(<Login />)} />
                        <Route path={ROUTES.REGISTER} element={wrapRoute(<Register />)} />
                        <Route path={ROUTES.UNAUTHORIZED} element={wrapRoute(<Unauthorized />)} />
                    </Route>

                    {/* Protected routes */}
                    <Route element={<ProtectedRoute />}>
                        <Route element={<MainLayout />}>
                            <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.TEMPLATES} replace />} />
                            <Route path={ROUTES.USERS} element={wrapRoute(<UserManagement />)} />
                            <Route path={ROUTES.SYSTEM_PROMPT} element={wrapRoute(<SystemPrompt />)} />
                            <Route path={ROUTES.REFERENCE_LIST_CONFIGURATION_NEW} element={wrapRoute(<ReferenceListForm />)} />
                            <Route path={ROUTES.REFERENCE_LIST_CONFIGURATION_DATA} element={wrapRoute(<ReferenceListData />)} />
                            <Route path={ROUTES.REFERENCE_LIST_CONFIGURATION_EDIT} element={wrapRoute(<ReferenceListForm />)} />
                            <Route path={ROUTES.REFERENCE_LIST_CONFIGURATION} element={wrapRoute(<ReferenceListConfiguration />)} />
                            <Route path={ROUTES.ZONES_CONFIGURATION_NEW} element={wrapRoute(<ZoneConfigurationForm />)} />
                            <Route path={ROUTES.ZONES_CONFIGURATION_EDIT} element={wrapRoute(<ZoneConfigurationForm />)} />
                            <Route path={ROUTES.ZONES_CONFIGURATION} element={wrapRoute(<ZoneConfigurationList />)} />
                            <Route path={ROUTES.TEMPLATES} element={wrapRoute(<Templates />)} />
                            <Route path={ROUTES.TEMPLATE_DETAIL} element={wrapRoute(<TemplateDetail />)} />
                            <Route path={ROUTES.REPORT_CATEGORY_CONFIG} element={wrapRoute(<TemplateCategoryConfig />)} />
                            <Route path={ROUTES.MOCK} element={wrapRoute(<RulesManagement />)} />
                            <Route path={ROUTES.PROFILE} element={wrapRoute(<Profile />)} />
                            <Route path={ROUTES.SETTINGS} element={wrapRoute(<Settings />)} />
                            <Route path={ROUTES.SHOWCASE} element={wrapRoute(<ComponentsShowcase />)} />
                            <Route path={ROUTES.DOCS} element={wrapRoute(<ArchitectureDocs />)} />
                        </Route>
                    </Route>

                    {/* 404 route */}
                    <Route path={ROUTES.NOT_FOUND} element={wrapRoute(<NotFound />)} />
                </Routes>
            </Suspense>
        </ErrorBoundary>
    );
};

export default AppRoutes;
